import { compareKittingCartSlotsByDueDate, type KittingCartSlot } from "./kittingCartModel";
import type { KittingPickLine } from "./kittingPickModel";

export const KITTING_PICK_SLOTS_QUERY_KEY = "slots";

/** Max optional co-located jobs shown in the order-selection modal (anchor is separate). */
export const MAX_CO_LOCATED_SLOT_SUGGESTIONS = 6;

export interface CoLocatedSlotRanking {
  /** |anchorStops ∩ candidateStops| */
  overlapCount: number;
  /** |candidateStops \ anchorStops| — storage stops the candidate adds beyond the anchor */
  additionalStops: number;
}

/** Storage locations used by pick lines for a cart slot. */
export function storageLocationsForCartSlot(
  pickLines: readonly KittingPickLine[],
  slotId: string,
): Set<string> {
  const locations = new Set<string>();
  for (const line of pickLines) {
    if (line.destinationSlotId === slotId) {
      locations.add(line.storageLocation);
    }
  }
  return locations;
}

/** Ranking metrics for a co-located candidate relative to the anchor slot. */
export function coLocatedSlotRanking(params: {
  anchorSlotId: string;
  candidateSlotId: string;
  pickLines: readonly KittingPickLine[];
}): CoLocatedSlotRanking {
  const anchorLocations = storageLocationsForCartSlot(params.pickLines, params.anchorSlotId);
  const candidateLocations = storageLocationsForCartSlot(params.pickLines, params.candidateSlotId);
  let overlapCount = 0;
  for (const location of anchorLocations) {
    if (candidateLocations.has(location)) overlapCount++;
  }
  let additionalStops = 0;
  for (const location of candidateLocations) {
    if (!anchorLocations.has(location)) additionalStops++;
  }
  return { overlapCount, additionalStops };
}

function compareCoLocatedCandidates(
  left: { slot: KittingCartSlot; ranking: CoLocatedSlotRanking },
  right: { slot: KittingCartSlot; ranking: CoLocatedSlotRanking },
): number {
  if (right.ranking.overlapCount !== left.ranking.overlapCount) {
    return right.ranking.overlapCount - left.ranking.overlapCount;
  }
  if (left.ranking.additionalStops !== right.ranking.additionalStops) {
    return left.ranking.additionalStops - right.ranking.additionalStops;
  }
  return compareKittingCartSlotsByDueDate(left.slot, right.slot);
}

/**
 * Other cart slots that share at least one storage stop with the anchor, ranked by:
 * 1. largest overlap with anchor stops (desc)
 * 2. fewest additional stops beyond anchor (asc)
 * 3. earliest due date (asc)
 *
 * Returns at most {@link MAX_CO_LOCATED_SLOT_SUGGESTIONS}; candidates with zero overlap are excluded.
 */
export function coLocatedCartSlots(params: {
  anchorSlotId: string;
  pickLines: readonly KittingPickLine[];
  cartSlots: readonly KittingCartSlot[];
}): KittingCartSlot[] {
  const { anchorSlotId, pickLines, cartSlots } = params;
  const anchorLocations = storageLocationsForCartSlot(pickLines, anchorSlotId);
  if (anchorLocations.size === 0) return [];

  const slotIdsAtSharedLocations = new Set<string>();
  for (const line of pickLines) {
    if (line.destinationSlotId === anchorSlotId) continue;
    if (anchorLocations.has(line.storageLocation)) {
      slotIdsAtSharedLocations.add(line.destinationSlotId);
    }
  }

  const candidates = cartSlots
    .filter((slot) => slot.slotKey !== anchorSlotId && slotIdsAtSharedLocations.has(slot.slotKey))
    .map((slot) => ({
      slot,
      ranking: coLocatedSlotRanking({
        anchorSlotId,
        candidateSlotId: slot.slotKey,
        pickLines,
      }),
    }))
    .filter(({ ranking }) => ranking.overlapCount > 0)
    .sort(compareCoLocatedCandidates)
    .slice(0, MAX_CO_LOCATED_SLOT_SUGGESTIONS)
    .map(({ slot }) => slot);

  return candidates;
}

export function sharedStorageLocationsBetweenSlots(params: {
  anchorSlotId: string;
  otherSlotId: string;
  pickLines: readonly KittingPickLine[];
}): string[] {
  const anchorLocations = storageLocationsForCartSlot(params.pickLines, params.anchorSlotId);
  const otherLocations = storageLocationsForCartSlot(params.pickLines, params.otherSlotId);
  const shared: string[] = [];
  for (const location of anchorLocations) {
    if (otherLocations.has(location)) {
      shared.push(location);
    }
  }
  return shared.sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
}

export function parseKittingPickSlotIds(raw: string | null | undefined): string[] {
  if (raw == null || raw.trim() === "") return [];
  const seen = new Set<string>();
  const slotIds: string[] = [];
  for (const part of raw.split(",")) {
    const trimmed = part.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) continue;
    seen.add(trimmed);
    slotIds.push(trimmed);
  }
  return slotIds;
}

export function buildKittingPickSlotsQuery(slotIds: readonly string[]): string {
  return slotIds.join(",");
}

export function filterPickLinesBySlotIds(
  pickLines: readonly KittingPickLine[],
  slotIds: readonly string[],
): KittingPickLine[] {
  if (slotIds.length === 0) return [];
  const allowed = new Set(slotIds);
  return pickLines.filter((line) => allowed.has(line.destinationSlotId));
}
